import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Share2, Plus, Copy, ExternalLink, Trash2, Loader2 } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

/**
 * In-modal public share-link control, shared by the single-submission viz modal and the list-view modal so
 * sharing lives in ONE place (no separate page card). It is intentionally backend-agnostic: the caller passes an
 * `api` with `list` / `create(expiresInDays)` / `revoke(shareId)` so this component is identical for both kinds —
 * single submissions mint a `/v/{token}` link, list views mint a `/v/list/{token}` link with the config captured.
 *
 * Renders a "Share link" button; clicking opens a popover (anchored above, since it lives in a modal footer) to
 * create / copy / open / revoke links. Each created link is copied to the clipboard immediately.
 *
 * @param {{ api: { list: () => Promise<any[]>, create: (expiresInDays: number|null) => Promise<any>,
 *   revoke: (shareId: number) => Promise<any> }, label?: string }} props
 */
export function VizShareLinks({ api, label = 'Share link' }) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [expiresInDays, setExpiresInDays] = useState(''); // '' = never
  const ref = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.list();
      setShares(Array.isArray(data) ? data : []);
    } catch {
      setShares([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { if (open) load(); }, [open, load]);

  // Close the popover on an outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.info('Link copied to clipboard'); }
    catch { toast.info(url); }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const days = expiresInDays === '' ? null : parseInt(expiresInDays, 10);
      const created = await api.create(days);
      toast.success('Share link created');
      if (created?.url) copy(created.url);
      await load();
    } catch {
      toast.error('Could not create share link');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (shareId) => {
    if (!window.confirm('Revoke this link? Anyone holding it will lose access immediately.')) return;
    try { await api.revoke(shareId); toast.success('Link revoked'); await load(); }
    catch { toast.error('Could not revoke link'); }
  };

  const statusOf = (s) => {
    if (s.revokedAt) return 'Revoked';
    if (s.expiresAt && new Date(s.expiresAt) <= new Date()) return 'Expired';
    return 'Active';
  };

  return (
    <Wrap ref={ref}>
      <ShareBtn theme={theme} type="button" onClick={() => setOpen((o) => !o)}>
        <Share2 size={14} /> {label}
      </ShareBtn>

      {open && (
        <Popover theme={theme} onClick={(e) => e.stopPropagation()}>
          <PopHead theme={theme}>Public share links</PopHead>
          <PopHint theme={theme}>Anyone with the link can view this — no login needed.</PopHint>

          <CreateRow>
            <Select theme={theme} value={expiresInDays} onChange={(e) => setExpiresInDays(e.target.value)} disabled={creating} aria-label="Link expiry">
              <option value="">Never expires</option>
              <option value="7">Expires in 7 days</option>
              <option value="30">Expires in 30 days</option>
              <option value="90">Expires in 90 days</option>
            </Select>
            <CreateBtn theme={theme} type="button" onClick={handleCreate} disabled={creating}>
              {creating ? <Loader2 size={14} className="spin" /> : <Plus size={14} />}
              {creating ? 'Creating…' : 'Create link'}
            </CreateBtn>
          </CreateRow>

          {loading ? (
            <Muted theme={theme}>Loading links…</Muted>
          ) : shares.length === 0 ? (
            <Muted theme={theme}>No links yet.</Muted>
          ) : (
            <List>
              {shares.map((s) => (
                <Item key={s.id} theme={theme}>
                  <Status theme={theme} $revoked={!!s.revokedAt}>● {statusOf(s)}</Status>
                  <UrlRow theme={theme}>
                    <UrlText theme={theme} title={s.url}>{s.url}</UrlText>
                    <Actions>
                      <IconBtn theme={theme} type="button" title="Copy link" onClick={() => copy(s.url)}>
                        <Copy size={13} />
                      </IconBtn>
                      <IconBtn theme={theme} as="a" href={s.url} target="_blank" rel="noopener noreferrer" title="Open in new tab">
                        <ExternalLink size={13} />
                      </IconBtn>
                      {s.isActive && (
                        <IconBtn theme={theme} type="button" title="Revoke" $danger onClick={() => handleRevoke(s.id)}>
                          <Trash2 size={13} />
                        </IconBtn>
                      )}
                    </Actions>
                  </UrlRow>
                </Item>
              ))}
            </List>
          )}
        </Popover>
      )}
    </Wrap>
  );
}

const Wrap = styled.div`
  position: relative;
  display: inline-block;
`;

const ShareBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.primary};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover { border-color: ${p => p.theme.colors.primary}; }
`;

const Popover = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  z-index: 60;
  width: 360px;
  max-width: 90vw;
  background: ${p => p.theme.colors.background};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  box-shadow: ${p => p.theme.shadows.lg};
  padding: ${p => p.theme.spacing.md};
`;

const PopHead = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.theme.colors.text};
`;

const PopHint = styled.p`
  margin: 4px 0 ${p => p.theme.spacing.sm};
  font-size: 12px;
  color: ${p => p.theme.colors.textSecondary || p.theme.colors.muted};
`;

const CreateRow = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.sm};
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: ${p => p.theme.spacing.sm};
`;

const Select = styled.select`
  flex: 1;
  min-width: 120px;
  padding: 7px 9px;
  border-radius: ${p => p.theme.borderRadius.md};
  border: 1px solid ${p => p.theme.colors.border};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.text};
  font-size: 13px;
`;

const CreateBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 7px 12px;
  border: 1px solid ${p => p.theme.colors.primary};
  border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.theme.colors.primary};
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:disabled { opacity: 0.6; cursor: default; }
`;

const Muted = styled.div`
  font-size: 12px;
  color: ${p => p.theme.colors.textSecondary || p.theme.colors.muted};
  padding: ${p => p.theme.spacing.xs} 0;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.sm};
  max-height: 220px;
  overflow-y: auto;
`;

const Item = styled.div`
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  padding: 6px 8px;
`;

const Status = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${p => (p.$revoked ? (p.theme.colors.error || '#b3261e') : (p.theme.colors.success || '#1f9d55'))};
`;

const UrlRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  margin-top: 4px;
`;

const UrlText = styled.code`
  flex: 1;
  min-width: 0;
  font-size: 11px;
  color: ${p => p.theme.colors.text};
  background: ${p => p.theme.colors.backgroundTertiary || p.theme.colors.backgroundSecondary || p.theme.colors.background};
  padding: 5px 7px;
  border-radius: ${p => p.theme.borderRadius.sm};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Actions = styled.div`
  display: flex;
  gap: 4px;
  flex-shrink: 0;
`;

const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  background: ${p => p.theme.colors.background};
  color: ${p => (p.$danger ? (p.theme.colors.error || '#b3261e') : (p.theme.colors.textSecondary || p.theme.colors.muted))};
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s ease;
  &:hover { color: ${p => (p.$danger ? '#b3261e' : p.theme.colors.primary)}; border-color: ${p => p.theme.colors.primary}; }
`;

export default VizShareLinks;
